import * as authHooks from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import { useBrokerFeature } from "@rentiohq/shared-frontend/dist/redux/broker/broker.hooks";
import * as applicationHooks from "@rentiohq/shared-frontend/dist/reduxV2/applications/application.hooks";
import { EIntakeStep } from "@rentiohq/shared-frontend/dist/types/application.types";
import { EBrokerFeature } from "@rentiohq/shared-frontend/dist/types/broker.types";
import {
  EPropertyTypeId,
  IProperty,
} from "@rentiohq/shared-frontend/dist/types/property.types";
import { Loading } from "@rentiohq/web-shared/dist/components";
import { findLastIndex } from "lodash";
import { useNavigate } from "react-router-dom";
import { TSelectableItemProps } from "scenes/Properties/Properties.types";
import { getSteps } from "scenes/Properties/scenes/Applications/PropertyDetailApplications.util";
import PropertyListItem from "../PropertyListItem";
import { PropertyListItemItem } from "../PropertyListItem/PropertyListItem.header";
import * as S from "./PropertyListItemApplications.styles";

interface IPropertyListItemApplicationsProps {
  property: IProperty;
  selectedParentId?: number;
  onSetParentId: (parentId?: number) => void;
  selectable?: TSelectableItemProps;
}

const PropertyListItemApplications = (
  props: IPropertyListItemApplicationsProps,
) => {
  const { property, onSetParentId, selectable } = props;
  const navigate = useNavigate();

  // Redux
  const { broker } = authHooks.useSelf();

  const isGroup = property.typeId === EPropertyTypeId.Group;
  const { countSteps, isFetching } = applicationHooks.useCountSteps({
    shouldRefetch: true,
    query: isGroup
      ? undefined
      : {
          filter: {
            archivedAt: { is: null },
            propertyId: { eq: property.id },
          },
        },
  });

  const hasRentMatch = useBrokerFeature(EBrokerFeature.RentMatch);
  const hasCalendarSync = useBrokerFeature(EBrokerFeature.CalendarSync);

  // Helpers
  const intakeType = broker?.intakeType || 2;
  const steps = getSteps(intakeType, hasCalendarSync, hasRentMatch);

  // Render
  if (isGroup) {
    return (
      <PropertyListItem
        property={property}
        onSetParentId={onSetParentId}
        selectable={selectable}
      />
    );
  }

  if (countSteps?.steps) {
    const allSteps = steps.flat();

    const lastActiveStepIndex = findLastIndex(
      allSteps,
      step => !!countSteps.steps[step],
    );

    return (
      <PropertyListItem
        property={property}
        link={`/properties/${property.id}/applications`}
        onSetParentId={onSetParentId}
        selectable={selectable}
      >
        <S.PropertyListItemItemSpacer />
        {allSteps.map((step, index) => {
          const isFirst = index === 0;
          const isLast = allSteps.length - 1 === index;

          const isCurrentActive = index <= lastActiveStepIndex;
          const isNextActive = index + 1 <= lastActiveStepIndex;
          let count = countSteps.steps[step] || 0;

          if (isFirst && countSteps?.steps) {
            Object.keys(countSteps.steps).forEach(key => {
              const stepKey = +key;
              if (
                stepKey !== EIntakeStep.notEligible &&
                stepKey !== EIntakeStep.tenant &&
                !allSteps.includes(stepKey)
              ) {
                count += countSteps.steps[stepKey] || 0;
              }
            });
          }

          return (
            <PropertyListItemItem
              key={step}
              style={{
                flex: 1,
              }}
            >
              <S.InnerItemWrap>
                {!isFirst && <S.LineLeft isActive={isCurrentActive} />}
                {!isLast && <S.LineRight isActive={isNextActive} />}
                <S.Step
                  isActive={isCurrentActive}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    navigate(`/properties/${property.id}/applications`, {
                      state: { step },
                    });
                  }}
                >
                  {count}
                </S.Step>
              </S.InnerItemWrap>
            </PropertyListItemItem>
          );
        })}
        <S.PropertyListItemItemSpacer />
      </PropertyListItem>
    );
  }

  if (isFetching) {
    return (
      <PropertyListItem
        property={property}
        link={`/properties/${property.id}/applications`}
        onSetParentId={onSetParentId}
      >
        <S.LoadingWrap>
          <S.LoadingInnerWrap>
            <Loading asDots={true} />
          </S.LoadingInnerWrap>
        </S.LoadingWrap>
      </PropertyListItem>
    );
  }

  return (
    <PropertyListItem
      property={property}
      link={`/properties/${property.id}/applications`}
      onSetParentId={onSetParentId}
      selectable={selectable}
    />
  );
};

// eslint-disable-next-line import/no-default-export
export default PropertyListItemApplications;
